import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../contexts/SocketContext";
import { useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { FaBell } from "react-icons/fa";
import {
  getNotificationsAction,
  markNotificationAsReadAction,
  markAllNotificationsAsReadAction,
  deleteNotificationAction,
  addNotificationAction,
  updateNotificationCountAction,
} from "../../redux/actions/notificationActions";
import NotificationReasonModal from "../modals/NotificationReasonModal";
import { getUnreadNotificationCount } from "../../redux/api/userAPI";

const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { socket, isConnected } = useSocket();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reasonModal, setReasonModal] = useState({ open: false, title: "", reason: "", message: "", targetPath: null });

  const notifications = useSelector((state) => state.user?.notifications || []);
  const unreadCount = useSelector((state) => state.user?.unreadNotificationCount || 0);

  // Fetch notifications when dropdown opens, and mark all as read when opening
  useEffect(() => {
    if (!isOpen) return;

    if (unreadCount > 0) {
      dispatch(markAllNotificationsAsReadAction());
    }
    if (notifications.length === 0) {
      setIsLoading(true);
      dispatch(getNotificationsAction(20, 0)).finally(() => {
        setIsLoading(false);
      });
    }
  }, [isOpen, dispatch, notifications.length, unreadCount]);

  // Fetch unread count on mount
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const { error, data } = await getUnreadNotificationCount();
        if (!error && data) {
          dispatch(updateNotificationCountAction(data.count || 0));
        }
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    };

    fetchUnreadCount();
  }, [dispatch]);

  // Listen for real-time notifications
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (data) => {
      if (data.notification) {
        dispatch(addNotificationAction(data.notification));
        dispatch(updateNotificationCountAction(data.count || unreadCount + 1));
      }
    };

    const handleNotificationCount = (data) => {
      dispatch(updateNotificationCountAction(data.count || 0));
    };

    socket.on("new_notification", handleNewNotification);
    socket.on("notification_count", handleNotificationCount);

    return () => {
      socket.off("new_notification", handleNewNotification);
      socket.off("notification_count", handleNotificationCount);
    };
  }, [socket, isConnected, dispatch, unreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await dispatch(markNotificationAsReadAction(notification._id));
    }

    // Navigate based on notification type
    let targetPath = null;

    switch (notification.type) {
      case "follow_request_received":
        targetPath = "/skills-circle/received-follow-requests";
        break;
      case "profile_view_request_received":
        targetPath = "/skills-circle/received-profile-requests";
        break;
      case "follow_request_accepted":
        // Navigate to sender's profile
        if (notification.sender?.profileUrl) {
          targetPath = `/profile/${notification.sender.profileUrl}`;
        } else if (notification.sender?._id) {
          targetPath = `/profile/${notification.sender._id}`;
        }
        break;
      case "profile_view_request_accepted":
        // Navigate to sender's profile
        if (notification.sender?.profileUrl) {
          targetPath = `/profile/${notification.sender.profileUrl}`;
        } else if (notification.sender?._id) {
          targetPath = `/profile/${notification.sender._id}`;
        }
        break;
      case "post_liked":
      case "post_commented":
      case "comment_replied":
        if (notification.link) {
          targetPath = notification.link;
        } else if (notification.relatedEntityId) {
          targetPath = `/post/${notification.relatedEntityId}`;
        }
        break;
      case "post_reported":
      case "post_reinstated":
        if (notification.link) {
          targetPath = notification.link;
        } else if (notification.relatedEntityId) {
          targetPath = `/post/${notification.relatedEntityId}`;
        }
        setReasonModal({
          open: true,
          title: notification.title || "Notification",
          reason: notification.reason,
          message: notification.message,
          targetPath,
        });
        return;
      case "post_taken_down":
        targetPath = "/skills-feed";
        setReasonModal({
          open: true,
          title: notification.title || "Post Removed",
          reason: notification.reason,
          message: notification.message,
          targetPath,
        });
        return;
      default:
        if (notification.link) {
          targetPath = notification.link;
        }
    }

    if (targetPath) {
      navigate(targetPath);
      setIsOpen(false);
    }
  };

  const handleReasonModalClose = () => {
    setReasonModal((prev) => ({ ...prev, open: false }));
    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllNotificationsAsReadAction());
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    await dispatch(deleteNotificationAction(notificationId));
  };

  const formatTime = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return notificationDate.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-primary-700 transition-colors"
        aria-label="Notifications"
      >
        <FaBell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-action rounded-full">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <Transition
        show={isOpen}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-primary hover:text-primary-700 font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FaBell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.isRead ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      {notification.sender?.avatar ? (
                        <img
                          src={notification.sender.avatar}
                          alt={notification.sender.name}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                          <FaBell className="w-5 h-5 text-gray-400" />
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatTime(notification.createdAt)}
                            </p>
                          </div>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => handleDeleteNotification(notification._id, e)}
                        className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
                        aria-label="Delete notification"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  navigate("/notifications");
                  setIsOpen(false);
                }}
                className="text-sm text-primary-700 hover:text-primary-800 font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      </Transition>

      <NotificationReasonModal
        isOpen={reasonModal.open}
        onClose={handleReasonModalClose}
        title={reasonModal.title}
        reason={reasonModal.reason}
        message={reasonModal.message}
      />
    </div>
  );
};

export default NotificationBell;
